<template>
  <adn-modal
    class="p-info-request-container"
    ref="contactMyTCModal"
    size="normal"
    :title="i18n._('Demander un prix')"
    @afterHide="onCloseModal"
  >
    <template #body>
      <div class="p-info-request-body">
        <div class="p-info-request-body__row p-info-request-header">
          <img class="p-info-request-header__product_image" :src="productInfo.image" alt="product image" />
          <div class="p-info-request-header__product_description">
            <p class="p-info-request-header__product_description__name">{{ productInfo.name }}</p>
            <p class="p-info-request-header__product_description__brand">{{ productInfo.brand }}</p>
          </div>
        </div>
        <template v-if="formMode">
          <div class="p-info-request-body__row p-info-request__technical-sales">
            <label class="upper-title" for="request-technical-sales">
              {{ i18n._('Choisissez votre interlocuteur') }}
              <span v-if="form.fields.contact.required" class="p-info-request__input--mandatory">*</span>
            </label>
            <select
              id="request-technical-sales"
              v-model="form.fields.contact.value"
              @blur="form.verifyField(form.fields.contact)"
            >
              <option v-for="technicalSale in technicalSales" :key="technicalSale.uuid" :value="technicalSale">
                {{ technicalSale.first_name }} {{ technicalSale.last_name }}
              </option>
            </select>
            <span v-if="!form.isFieldValid('contact')" class="p-info-request__input--error">
              {{ i18n._('Un interlocuteur doit être choisi') }}
            </span>
          </div>
          <div class="p-info-request-body__row">
            <p class="p-info-request-body__row__mainLabel upper-title">
              {{ i18n._('Choisissez une surface et/ou une quantité') }}
            </p>
            <div class="p-info-request-body__row__request">
              <div class="p-info-request-body__row__request__quantity">
                <label for="request-quantity">
                  {{ i18n._('Quantité souhaitée') }}
                  <span v-if="form.fields.quantity.required" class="p-info-request__input--mandatory">*</span>
                </label>
                <input
                  id="request-quantity"
                  type="number"
                  v-model="form.fields.quantity.value"
                  @blur="form.verifyField(form.fields.quantity)"
                />
                <span v-if="!form.isFieldValid('quantity')" class="p-info-request__input--error">
                  {{ i18n._('quantité non valide') }}
                </span>
              </div>
              <div class="p-info-request-body__row__request__unit">
                <label>
                  {{ i18n._('Unité ') }}
                  <span v-if="form.fields.unit.required" class="p-info-request__input--mandatory">*</span>
                </label>
                <select id="request-unit" v-model="form.fields.unit.value" @blur="form.verifyField(form.fields.unit)">
                  <option v-for="unit in productInfo.units" :key="unit" :value="unit">
                    {{ unit }}
                  </option>
                </select>
                <span v-if="!form.isFieldValid('unit')" class="p-info-request__input--error">unité non valide</span>
              </div>
              <template v-if="showSurface">
                <div class="p-info-request-body__row__request__separator"></div>
                <div class="p-info-request-body__row__request__surface">
                  <label for="request-surface">
                    {{ i18n._('Surface concernée en ha') }}
                    <span v-if="form.fields.surface.required" class="p-info-request__input--mandatory">*</span>
                  </label>
                  <input
                    id="request-surface"
                    type="number"
                    v-model="form.fields.surface.value"
                    @blur="form.verifyField(form.fields.surface)"
                  />
                  <span v-if="!form.isFieldValid('surface')" class="p-info-request__input--error">
                    {{ i18n._('surface non valide') }}
                  </span>
                </div>
              </template>
            </div>
          </div>
          <div class="p-info-request-body__row">
            <label for="request-additional-info">
              {{ i18n._('Merci de préciser votre demande') }}
              <span v-if="form.fields.message.required" class="p-info-request__input--mandatory">*</span>
            </label>
            <textarea
              id="request-additional-info"
              type="number"
              v-model="form.fields.message.value"
              @blur="form.verifyField(form.fields.message)"
              @input="form.verifyField(form.fields.message)"
            ></textarea>
            <span v-if="!form.isFieldValid('message')" class="p-info-request__input--error">message non valide</span>
          </div>
        </template>
        <template v-else>
          <div class="sent-message">
            <template v-if="sentError">
              <span class="adn-icon adn-icon--status-deleted p-ifo-request__input--error"></span>
              <p>{{ i18n._(`Erreur lors de l'envoi de votre demande,`) }}</p>
              <p>{{ i18n._('veuillez essayer ultérieurement.') }}</p>
            </template>
            <template v-else>
              <span class="adn-icon adn-icon--status-active"></span>
              <p>{{ i18n._('Votre demande a bien été envoyée,') }}</p>
              <p>{{ i18n._('vous avez reçu une copie par email.') }}</p>
            </template>
          </div>
        </template>
      </div>
    </template>
    <template #footer>
      <template v-if="formMode">
        <AdnButton kind="secondary" class="tertiary" @click="hide">{{ i18n._('Annuler') }}</AdnButton>
        <AdnButton kind="primary" @click="onSendRequest" :disabled="!form.isValid()">
          {{ i18n._('Envoyer la demande') }}
        </AdnButton>
      </template>
      <template v-else>
        <AdnButton class="continue-button" @click="hide">{{ i18n._('Continuer mes achats') }}</AdnButton>
      </template>
    </template>
  </adn-modal>
</template>

<script lang="ts">
import AdnButton from '@b2ag/design-system/src/components/AdnButton/AdnButton.vue'
import AdnModal from '@b2ag/design-system/src/components/AdnModal/AdnModal.vue'
import { i18n } from '@b2ag/locale'
import type { PropType } from 'vue'
import { defineComponent, ref, type Ref, computed } from 'vue'
import { Form } from '@b2ag/utils/src/form'
import { useSendProductInfoRequest } from '@b2ag/product/src/composables/useSendProductInfoRequest'
import { useStore } from 'vuex'
import { isAladin, isFarmi } from '@b2ag/app-brands'
import { useAnalyticService } from '@b2ag/plugin-aladin-gtm'

type TechnicalSaleInfo = { uuid: string; first_name: string; last_name: string; email: string }

export default defineComponent({
  name: 'PInfoRequestToTechnicalSaleModal',
  components: { AdnModal, AdnButton },
  props: {
    technicalSales: {
      type: Array<TechnicalSaleInfo>,
      default: () => [],
    },
    productInfo: {
      type: Object as PropType<{ id: string; name: string; image: string; brand: string; units: string[] }>,
      default: () => {},
    },
  },
  setup(props) {
    const store = useStore()
    const contactMyTCModal = ref<(typeof AdnModal & { show: () => void; hide: () => void }) | null>(null)
    const formMode = ref(true)
    const sentError = ref(false)
    const form: Ref<Form> = ref(
      new Form({
        quantity: { value: null, required: false, pattern: /^[0-9]*$/ },
        unit: { value: null, required: false },
        surface: { value: null, required: false, pattern: /^[0-9]*$/ },
        message: { value: null, required: false },
        contact: { value: props.technicalSales[0] ?? null, required: false },
      }),
    )
    const showSurface = ref(isAladin || isFarmi)

    const doSendRequest = useSendProductInfoRequest()

    function show() {
      contactMyTCModal.value?.show()
    }

    function hide() {
      contactMyTCModal.value?.hide()
    }

    const currentProfile = computed(() => {
      return store.getters.currentProfile
    })

    function onCloseModal() {
      formMode.value = true
      sentError.value = false
      form.value.fields.quantity.value = null
      form.value.fields.unit.value = null
      form.value.fields.surface.value = null
      form.value.fields.message.value = null
      form.value.fields.contact.value = props.technicalSales[0] ?? null
    }

    async function onSendRequest() {
      sentError.value = false
      const payload = {
        to: {
          email: form.value.fields.contact?.value?.email,
          id: form.value.fields.contact?.value?.uuid,
        },
        from: {
          email: currentProfile.value.email,
          firstName: currentProfile.value.firstName,
          companyName: currentProfile.value.companyName,
          lastName: currentProfile.value.lastName,
          phoneNumber: currentProfile.value.phoneNumber,
        },
        product: {
          id: props.productInfo.id,
          label: props.productInfo.name || undefined,
          brand: props.productInfo.brand || undefined,
          image: props.productInfo.image || undefined,
          quantity: form.value.fields.quantity?.value?.toString() || undefined,
          unit: form.value.fields.unit?.value || undefined,
          surface: form.value.fields.surface?.value?.toString() || undefined,
        },
        text: form.value.fields.message?.value || undefined,
      }
      const res = await doSendRequest(payload)
      if (!res?.status) {
        sentError.value = true
      }
      formMode.value = false
      useAnalyticService().trackEvent('click_send_ask_price', {})
    }

    return {
      contactMyTCModal,
      formMode,
      form,
      i18n,
      hide,
      onCloseModal,
      onSendRequest,
      sentError,
      show,
      showSurface,
    }
  },
})
</script>

<style lang="scss" scoped>
.p-info-request-container {
  display: flex;
  flex-direction: column;
  border-radius: var(--border-radius-3);
  padding: var(--spacing-4);
  gap: var(--spacing-4);

  :deep(.adn-modal__dialog) {
    width: 100%;
    max-width: 60rem;

    // surcharge specifique de largeur de modale
    @media (min-width: $breakpoint-sm) {
      max-width: 60rem;
    }

    h1 {
      font-size: var(--font-size-xl);
    }
  }

  .upper-title {
    font-size: var(--font-size-l);
    line-height: var(--line-height-m);
    font-weight: bold;
  }

  .p-info-request__input--error,
  .p-info-request__input--mandatory {
    color: var(--color-error);
  }

  .p-info-request-body {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    label {
      display: block;
      font-size: var(--font-size-m);
      line-height: var(--line-height-m);
    }

    input,
    textarea,
    select {
      line-height: var(--line-height-m);
      font-size: var(--font-size-m);
      padding: var(--spacing-2);
      border-radius: var(--border-radius-1);
      border: 1px solid var(--text-colors-neutral-neutral-400, #9ba6ac);
      width: 100%;
    }

    select {
      padding: 0.7rem;
    }

    textarea {
      display: block;
      min-height: 11.5rem;
    }

    .p-info-request-header {
      background-color: var(--color-blue-lighter);
      border-radius: var(--border-radius-2);
      padding: var(--spacing-2);
      display: flex;
      column-gap: var(--spacing-3);

      @media (min-width: $breakpoint-md) {
        padding: var(--spacing-3);
      }

      img {
        display: inline-block;
        height: 8rem;
        border-radius: var(--border-radius-2);
        background-color: var(--color-white);
      }

      .p-info-request-header__product_description__name {
        font-weight: bold;
      }
    }

    .p-info-request__technical-sales {
      label.upper-title {
        font-size: var(--font-size-l);
      }

      @media (min-width: $breakpoint-md) {
        select {
          max-width: 50%;
        }
      }
    }

    .p-info-request-body__row__mainLabel {
      margin-bottom: var(--spacing-2);
    }

    .p-info-request-body__row__request {
      display: flex;
      flex-wrap: wrap;
      row-gap: var(--spacing-4);

      .p-info-request-body__row__request__quantity {
        margin-right: var(--spacing-2);
        flex: 3;

        @media (min-width: $breakpoint-md) {
          flex: 2;
        }
      }

      .p-info-request-body__row__request__unit {
        flex: 2;

        @media (min-width: $breakpoint-md) {
          flex: 1;
        }
      }

      .p-info-request-body__row__request__separator {
        display: none;

        @media (min-width: $breakpoint-md) {
          display: inline-block;
        }

        margin-inline: var(--spacing-4);
        border-right: 1px solid var(--color-black);
      }

      .p-info-request-body__row__request__surface {
        width: 100%;

        @media (min-width: $breakpoint-md) {
          flex: 2;
        }
      }
    }
  }

  :deep(.adn-modal__footer) {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-2) var(--spacing-4);

    .adn-button {
      margin: 0;
      width: 100%;

      &.tertiary {
        background-color: unset;
        border: none;
      }
    }

    @media (min-width: $breakpoint-md) {
      .adn-button {
        flex: 1;

        &.continue-button {
          margin: auto;
          max-width: 28rem;
        }
      }
    }
  }

  .sent-message {
    text-align: center;

    .adn-icon--status-active {
      color: var(--color-success);
    }

    p {
      font-size: var(--font-size-l);
    }
  }
}
</style>
